import type { components } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";

type Schemas = components["schemas"];
export type SetupItem = Schemas["WorkflowSetupItemOut"];
export type SetupBinding = Schemas["WorkflowSetupBindingOut"];
export type SetupStatus = Schemas["WorkflowSetupStatus"];

/** i18next's `t`, as far as these helpers need it. */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

const S = "engage.workflows.setup";
const DAYS = "engage.workflows.builder.customSchedule.days";
const WEEK = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

/** Where the member is and what the checklist may link to. */
export interface SetupContext {
  slug: string;
  workflowName: string;
  /** Any agent of the workspace: a place to send the member when the item names none. */
  fallbackAgentId: string | undefined;
}

export interface SetupFix {
  href: string;
  label: string;
}

/** One line of the checklist, ready to render (text already translated). */
export interface SetupRow {
  id: string;
  status: SetupStatus;
  text: string;
  /** Ordered how-to lines under the sentence (the Home option steps). */
  steps: string[];
  /** Where to fix it; `null` for a done item or when no page applies. */
  fix: SetupFix | null;
}

interface Described {
  variant?: string | undefined;
  params?: Record<string, unknown> | undefined;
  steps?: string[] | undefined;
  fix?: SetupFix | null | undefined;
}

export const isOpen = (item: SetupItem): boolean => item.status !== "done";

const link = (href: string | null, label: string, t: Translate): SetupFix | null => (href ? { href, label: t(`${S}.fix.${label}`) } : null);

/** The agent a Home option fix opens: the option nearest to working, else an agent the workflow is meant for. */
function homeAgentId(item: SetupItem, ctx: SetupContext): string | undefined {
  const bindings = item.bindings ?? [];
  const nearest = bindings.find((binding) => binding.reason === item.reason) ?? bindings[0];
  return nearest?.agent_id ?? item.agents?.[0]?.id ?? ctx.fallbackAgentId;
}

const workingBindings = (item: SetupItem): SetupBinding[] => (item.bindings ?? []).filter((binding) => binding.reason === null || binding.reason === undefined);

function describeHome(item: SetupItem, ctx: SetupContext, t: Translate): Described {
  const agentId = homeAgentId(item, ctx);
  const fix = link(agentId ? consoleRoutes.widgetStudio(ctx.slug, agentId, "home") : null, "home", t);
  const bindings = item.bindings ?? [];
  if (bindings.length === 0) {
    const steps = ["publish", "option", "widget"].map((step) => t(`${S}.homeSteps.${step}`, { name: ctx.workflowName }));
    return { params: { name: ctx.workflowName }, steps, fix };
  }
  const shown = item.status === "done" ? workingBindings(item) : bindings;
  const nearest = shown.find((binding) => binding.reason === item.reason) ?? shown[0];
  const more = shown.length - 1;
  return {
    variant: item.status === "done" && more > 0 ? "done_more" : undefined,
    params: { label: nearest?.label ?? "", agent: nearest?.agent_name ?? "", count: more },
    fix: item.status === "done" ? null : fix,
  };
}

/** Weekday keys as "Monday – Friday" when they run on, else a list. */
function daysText(days: readonly string[], t: Translate): string {
  const ordered = WEEK.filter((day) => days.includes(day));
  const first = ordered[0];
  const last = ordered.at(-1);
  const runsOn = first !== undefined && last !== undefined && ordered.length > 1 && WEEK.indexOf(last) - WEEK.indexOf(first) + 1 === ordered.length;
  if (runsOn) return `${t(`${DAYS}.${first}`)} – ${t(`${DAYS}.${last}`)}`;
  return ordered.map((day) => t(`${DAYS}.${day}`)).join(", ");
}

function describeHours(item: SetupItem, ctx: SetupContext, t: Translate): Described {
  const hours = item.default_hours;
  const agent = item.agents?.find((candidate) => candidate.configured === false) ?? item.agents?.[0];
  const agentId = agent?.id ?? ctx.fallbackAgentId;
  return {
    params: { timezone: item.timezone ?? "", days: hours ? daysText(hours.days, t) : "", from: hours?.opens_at ?? "", to: hours?.closes_at ?? "" },
    fix: item.status === "done" ? null : link(agentId ? consoleRoutes.agentSettings(ctx.slug, agentId, "escalation") : null, "hours", t),
  };
}

function describeIdentity(item: SetupItem, ctx: SetupContext, t: Translate): Described {
  const agent = item.agents?.find((candidate) => candidate.configured === false) ?? item.agents?.[0];
  const agentId = agent?.id ?? ctx.fallbackAgentId;
  return { fix: item.status === "done" ? null : link(agentId ? consoleRoutes.widgetStudio(ctx.slug, agentId, "security") : null, "identity", t) };
}

/** The assistants with no default team or person are named; the fix opens the first one's Escalation & handoff settings. */
function describeHandoffSettings(item: SetupItem, ctx: SetupContext, t: Translate): Described {
  const missing = (item.agents ?? []).filter((agent) => agent.configured === false);
  const agentId = missing[0]?.id ?? ctx.fallbackAgentId;
  return {
    params: { agents: missing.map((agent) => agent.name).join(", ") },
    fix: item.status === "done" ? null : link(agentId ? consoleRoutes.agentSettings(ctx.slug, agentId, "escalation") : null, "handoff", t),
  };
}

/** Round-robin is its own sentence; a default owner who cannot take leads says why (`reason`) and links to Leads, where the routing is set. */
function describeLeadsRouting(item: SetupItem, ctx: SetupContext, t: Translate): Described {
  return {
    variant: item.reason ?? (item.routing === "round_robin" ? "done_round_robin" : undefined),
    params: { name: item.subject ?? "" },
    fix: item.status === "done" ? null : link(consoleRoutes.leads(ctx.slug), "leads", t),
  };
}

/** Never a problem; with no endpoint taking events the sentence says there is nothing to send yet and points at Webhooks. */
function describeEndpoints(item: SetupItem, ctx: SetupContext, t: Translate): Described {
  const count = item.endpoint_count ?? 0;
  return {
    variant: count === 0 ? "done_none" : undefined,
    params: { count },
    fix: count === 0 ? link(consoleRoutes.settings(ctx.slug, "webhooks"), "webhooks", t) : null,
  };
}

function describeScope(item: SetupItem, t: Translate): Described {
  const agents = item.agents ?? [];
  const channels = item.channels ?? [];
  return {
    params: {
      assistants: agents.length === 0 ? t(`${S}.scope.allAssistants`) : agents.map((agent) => agent.name).join(", "),
      channels: channels.length === 0 ? t(`${S}.scope.allChannels`) : channels.map((channel) => t(`engage.channels.${channel}`)).join(", "),
    },
  };
}

/** The fix link of a reference item (nothing to fix once it is done). */
function fixFor(item: SetupItem, ctx: SetupContext, t: Translate): SetupFix | null {
  if (item.status === "done") return null;
  const agentId = item.agent_id ?? ctx.fallbackAgentId;
  switch (item.key) {
    case "booking_action":
    case "lead_form":
      return link(agentId ? consoleRoutes.actions(ctx.slug, agentId) : null, "actions", t);
    case "endpoint":
      return link(consoleRoutes.settings(ctx.slug, "webhooks"), "webhooks", t);
    case "team":
    case "member":
      return link(consoleRoutes.team(ctx.slug), "team", t);
    case "lead_stage":
      return link(consoleRoutes.leads(ctx.slug), "leads", t);
    default:
      return null;
  }
}

function describeItem(item: SetupItem, ctx: SetupContext, t: Translate): Described {
  switch (item.key) {
    case "home_option":
      return describeHome(item, ctx, t);
    case "office_hours":
      return describeHours(item, ctx, t);
    case "identity_verification":
      return describeIdentity(item, ctx, t);
    case "handoff_settings":
      return describeHandoffSettings(item, ctx, t);
    case "leads_routing":
      return describeLeadsRouting(item, ctx, t);
    case "endpoints":
      return describeEndpoints(item, ctx, t);
    case "scope":
      return describeScope(item, t);
    case "endpoint":
      return { params: { name: item.subject ?? "", mode: t(`${S}.deliveryModes.${item.delivery_mode ?? "events"}`) }, fix: fixFor(item, ctx, t) };
    case "team":
      return { params: { name: item.subject ?? "", count: item.member_count ?? 0 }, fix: fixFor(item, ctx, t) };
    default:
      return { params: { name: item.subject ?? "", agent: item.agent_name ?? "" }, fix: fixFor(item, ctx, t) };
  }
}

/** One checklist item as a line: the sentence for its key and reason (`done` when it has none), the how-to steps and the fix link. */
export function describe(item: SetupItem, index: number, ctx: SetupContext, t: Translate): SetupRow {
  const described = describeItem(item, ctx, t);
  const variant = described.variant ?? item.reason ?? "done";
  return {
    id: `${item.key}:${item.subject_id ?? index}`,
    status: item.status,
    text: t(`${S}.items.${item.key}.${variant}`, described.params),
    steps: described.steps ?? [],
    fix: described.fix ?? null,
  };
}

/** What the trigger card says about the Home option that starts a `home_option` workflow. */
export type HomeStarter =
  | { kind: "started" | "linked"; label: string; agent: string; agentId: string }
  | { kind: "none"; agentId: string | undefined };

export function homeStarter(items: readonly SetupItem[], fallbackAgentId: string | undefined): HomeStarter | null {
  const home = items.find((item) => item.key === "home_option");
  if (!home) return null;
  const bindings = home.bindings ?? [];
  const working = workingBindings(home)[0];
  const shown = working ?? bindings.find((binding) => binding.reason === home.reason) ?? bindings[0];
  if (!shown) return { kind: "none", agentId: home.agents?.[0]?.id ?? fallbackAgentId };
  return { kind: working ? "started" : "linked", label: shown.label, agent: shown.agent_name, agentId: shown.agent_id };
}
